import { useState, useEffect } from 'react';
import mapFieldsToState from '../lib/mapFieldsToState';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import { isEqual } from 'lodash';
import { formatPriceInput } from '../services/formatPrice';
import usePrevious from '../hooks/usePrevious';

export default function useCheckoutForm({
  checkout,
  product,
  query,
  countryCode,
  onPayWhatYouWantPriceChange,
  draftCheckout,
  isPreview,
}) {
  const [form, setForm] = useState({
    discountCode: '',
    fields: checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
    variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
    ticketGroups:
      checkout.ticketGroups &&
      getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
    payWhatYouWant: query.pwyw ? Number(query.pwyw / 100) : null,
  });

  const previousQuery = usePrevious(query);

  // update the form state when the query changes
  // used to update fields, variants and pay what you want price
  useEffect(() => {
    if (isEqual(query, previousQuery)) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: query.pwyw ? Number(query.pwyw / 100) : null,
    }));
  }, [query]);

  // Update form fields when checkout fields change (only in preview mode)
  // Variants and ticket groups are mutated via other hooks.
  useEffect(() => {
    if (!isPreview) return;

    setForm(prevForm => ({
      ...prevForm,
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
    }));
  }, [checkout.fields, isPreview]);

  // update pay what you want price when form changes
  useEffect(() => {
    onPayWhatYouWantPriceChange?.(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant, onPayWhatYouWantPriceChange, product.currency]);

  return {
    form,
    setForm,
  };
}
